import { Alert, Currency, DatePickerPopover, InlineValuePopover, Input, Textarea } from "@xola/ui-kit";
import { DateFormat, formatDate, FormField, InputField, now } from "common";
import dayjs from "dayjs";
import { useField } from "formik";
import { produce } from "immer";
import { minBy } from "lodash";
import React, { useEffect, useState } from "react";
import { PaymentReminderType } from "types";
import { useDebounce, useDebouncedCallback } from "use-debounce";
import shallow from "zustand/shallow";
import { hasDeposit, isPackagePurchase } from "../../models/Purchase";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NoTranslate } from "../Common/NoTranslate";

export const CreatePurchaseInvoiceFields = () => {
    const { purchase, item, activeItemIndex } = useCreatePurchaseStore(
        (state) => state.getPurchaseAndActiveItem(),
        shallow,
    );
    const setPurchase = useCreatePurchaseStore((state) => state.actions.setPurchase);

    const earliestArrivalDateTime = useCreatePurchaseStore((state) => state.getEarliestArrivalDateTime());

    const { arrivalDateTime, paymentDueOffset } = item;

    const [days, setDays] = useState(paymentDueOffset ?? 0);
    const [daysPopover, setIsDaysPopoverOpen] = useState(false);
    const [depositDate, setDepositDate] = useState(now(earliestArrivalDateTime).subtract(days, "days"));

    const commentHelper = useField("comment");
    const arrivalOffsetHelper = useField("arrivalOffset");

    const reminder = purchase.paymentReminders?.find((reminder) => {
        const reminderType = [PaymentReminderType.INVOICE_TRAVELER, PaymentReminderType.SELLER_DEPOSIT];
        return reminderType.includes(reminder.type);
    });

    const reminderDate = reminder?.dueAt ?? dayjs.tz(arrivalDateTime);

    const purchaseDueNow = useCreatePurchaseStore((state) => state.dueNow);
    const depositAmount = purchase.payNow ?? purchaseDueNow ?? 0; // payNow is for deposit only, others should use dueNow

    const [debouncedDepositAmount] = useDebounce(depositAmount, 300);

    // const dateFormat = arrivalTime ? DateFormat.DATE_TIME_SHORT : DateFormat.DATE_LONG;
    const dateFormat = DateFormat.DATE_LONG;

    const arrivalDateOffset = earliestArrivalDateTime.subtract(days, "day");
    const arrivalOffset = dayjs.duration(days, "days").asMinutes();

    const invoiceComment = commentHelper[0].value;

    const setDebouncedInvoiceComment = useDebouncedCallback((comment: string) => {
        setPurchase((draft) => {
            if (draft.paymentReminders?.length) {
                draft.paymentReminders = draft.paymentReminders.map((reminder) => {
                    if (reminder.type === PaymentReminderType.INVOICE_TRAVELER) {
                        reminder.comment = comment;
                    }

                    return reminder;
                });
            }
        });
        useCreatePurchaseStore.setState(
            produce((draft) => {
                draft.paymentLineItem.payment.comment = comment;
            }),
        );
    }, 400);

    useEffect(() => {
        if (days === 0 && isPackagePurchase(purchase)) {
            const item = minBy(purchase.items, "arrivalDate");
            if (item) {
                setDepositDate(now(item.arrivalDate).subtract(days, "days"));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [purchase]);

    useEffect(() => {
        setPurchase((draft) => {
            const invoicePaymentReminders = [
                {
                    type: PaymentReminderType.INVOICE_SELLER,
                    arrivalOffset: Number(arrivalOffset),
                    dueAt: arrivalDateOffset,
                    ...(purchase?.payNow && { amount: purchase.payNow }),
                },
                {
                    type: PaymentReminderType.INVOICE_TRAVELER,
                    arrivalOffset: Number(arrivalOffset),
                    dueAt: arrivalDateOffset,
                    ...(purchase?.payNow && { amount: purchase.payNow }),
                },
            ];

            draft.paymentReminders = draft.paymentReminders?.length
                ? [
                      ...draft.paymentReminders.filter((reminder) => !invoiceReminderTypes.has(reminder.type)),
                      ...invoicePaymentReminders,
                  ]
                : invoicePaymentReminders;
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setDebouncedInvoiceComment(invoiceComment);
    }, [invoiceComment, setDebouncedInvoiceComment]);

    const updateTiming = (days: number) => {
        const offsetDays = Number(days ?? 0);

        setDays(offsetDays);
        setDepositDate(now(earliestArrivalDateTime).subtract(offsetDays, "days"));
        arrivalOffsetHelper[2].setValue(offsetDays);

        setPurchase((draft) => {
            draft.items[activeItemIndex].paymentDueOffset = offsetDays;
            if (draft.paymentReminders?.length) {
                draft.paymentReminders = draft.paymentReminders.map((reminder) => {
                    if (invoiceReminderTypes.has(reminder.type)) {
                        reminder.arrivalOffset = dayjs.duration(offsetDays, "days").asMinutes();
                        reminder.dueAt = earliestArrivalDateTime.subtract(offsetDays, "day");
                    }

                    return reminder;
                });
            }
        });
    };

    /**
     * Handles the change of the deposit date.
     *
     * @param value - The new date value form datepicker or from the state when the sected date is updated, use with the second param as true in that case.
     * @param keepOffset - A boolean indicating whether to keep the offset. Defaults to false. Is used to keep offset when the arrival date is changed.
     */

    const handleDepositDateChange = (value: Date, keepOffset = false) => {
        const shouldApplyOffset = keepOffset && paymentDueOffset;
        const selectedDate = shouldApplyOffset ? now(value).add(-paymentDueOffset, "days") : now(value);
        const daysDiff = now(earliestArrivalDateTime).startOf("day").diff(selectedDate.startOf("day"), "days");

        setDepositDate(selectedDate);
        updateTiming(daysDiff);
    };

    useEffect(() => {
        if (depositDate.add(days, "days").isSame(earliestArrivalDateTime)) {
            /**
             * If addition of (deposit due date + offset days) is same as the `earliestArrivalDateTime`, means earliestArrivalDateTime is not changed yet.
             * The `useEffect` execution may be happened cuz of initial render of this component in MIST flow, We don't want to do anything.
             **/
            return;
        }

        /** When the earliest arrival date for the purchase is changed, we also need to update the invoice due date value  */
        handleDepositDateChange(earliestArrivalDateTime.toDate(), true);

        // eslint-disable-next-line react-hooks/exhaustive-deps -- We need to execute this only on `earliestArrivalDateTime` change
    }, [earliestArrivalDateTime.toString()]);

    return (
        <>
            <FormField prevalidate className="order-1" name="arrivalOffset" label="Payment Due">
                <Currency currency={purchase.currency}>{debouncedDepositAmount}</Currency> due{" "}
                <InlineValuePopover
                    showArrow
                    text={days}
                    isOpen={daysPopover}
                    onClick={() => setIsDaysPopoverOpen(!daysPopover)}
                    onClickOutside={() => setIsDaysPopoverOpen(!daysPopover)}
                >
                    <Input
                        value={days ? days : ""}
                        type="number"
                        min="0"
                        step="1"
                        onChange={(e: any) => updateTiming(e.target.value)}
                    />
                </InlineValuePopover>
                day(s) before event
                <DatePickerPopover
                    popoverProps={{ placement: "auto" }}
                    value={depositDate.toDate()}
                    disabledDays={[
                        {
                            after: now(earliestArrivalDateTime).toDate(),
                            before: now().toDate(),
                        },
                    ]}
                    onChange={(value: Date) => handleDepositDateChange(value)}
                >
                    <span data-testid="payment-due-on">
                        &nbsp; (due on&nbsp;
                        <NoTranslate as="strong" className="underline hover:cursor-pointer">
                            {formatDate(depositDate, DateFormat.DATE_LONG)}
                        </NoTranslate>
                        )
                    </span>
                </DatePickerPopover>
            </FormField>
            <Alert className="order-3">
                <div className="space-y-2">
                    <h4 className="font-bold">Payment Timeline Summary</h4>

                    {depositAmount > 0 && hasDeposit(purchase) && (
                        <div data-testid="create-purchase-invoice-fields-deposit-due">
                            Deposit due
                            <NoTranslate className="mx-1 font-bold">
                                {formatDate(arrivalDateOffset, dateFormat)}
                            </NoTranslate>
                        </div>
                    )}

                    <div data-testid="create-purchase-invoice-fields-total-payment-due">
                        Total payment due{" "}
                        <NoTranslate className="mx-1 font-bold">{formatDate(reminderDate, dateFormat)}</NoTranslate>
                    </div>
                    {/* <div>
                        Event starts{" "}
                        <span className="mx-1 font-bold">{formatDate(dayjs(arrivalDateTime), dateFormat)}</span>
                    </div> */}
                </div>
            </Alert>

            <FormField className="order-last" name="comment" label="Message">
                <InputField as={Textarea} placeholder="Optional message to include in invoice" />
            </FormField>
        </>
    );
};

const invoiceReminderTypes = new Set([PaymentReminderType.INVOICE_TRAVELER, PaymentReminderType.INVOICE_SELLER]);
